<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import type { Player as CorePlayer } from '@tbc/core';
  import type { Rally } from '@tbc/core/score';
  import { openRearCamera, startRecording, type RecordingHandle } from '../lib/camera';
  import { PoseRunnerProxy } from '../lib/poseRunnerProxy';
  import { analyzeSession } from '../lib/swingAnalysis';
  import {
    db,
    newSessionId,
    type MatchState as ArchivalMatchState,
    type Player,
  } from '../lib/db';
  import PlayerSetup from '../lib/match/PlayerSetup.svelte';
  import Scoreboard from '../lib/match/Scoreboard.svelte';
  import RallyEndToast from '../lib/match/RallyEndToast.svelte';
  import MiniQuadra from '../lib/court/MiniQuadra.svelte';
  import type { CourtImpact, LivePlayers, LiveBall } from '../lib/court/types';
  import type { MatchConfig } from '../lib/match/types';
  import {
    awardPoint,
    emptyMatchState,
    type MatchState,
  } from '../lib/match/matchState';
  import { LiveAnalysisLoop } from '../lib/analysis/liveAnalysisLoop';
  import {
    getSettings,
    initSettings,
    snapshotSettings,
  } from '../lib/settings/settings.svelte';

  function pointToLabel(p: number, other: number): 0 | 15 | 30 | 40 | 'AD' {
    if (p >= 3 && other >= 3) return p === other + 1 ? 'AD' : 40;
    if (p === 0) return 0;
    if (p === 1) return 15;
    if (p === 2) return 30;
    return 40;
  }

  function toArchivalPlayer(p: CorePlayer): Player {
    return {
      id: p.id,
      displayName: p.name,
      dominantSide: p.handedness ?? 'right',
    };
  }

  function toArchivalMatchState(ui: MatchState): ArchivalMatchState {
    let gamesP1 = 0;
    let gamesP2 = 0;
    for (const g of ui.games) {
      if (g.winner === 'p1') gamesP1++;
      else gamesP2++;
    }
    return {
      setsWonP1: 0,
      setsWonP2: 0,
      gamesWonP1: gamesP1,
      gamesWonP2: gamesP2,
      currentPointP1: pointToLabel(ui.p1Points, ui.p2Points),
      currentPointP2: pointToLabel(ui.p2Points, ui.p1Points),
      isTiebreak: false,
    };
  }

  let videoEl = $state<HTMLVideoElement | null>(null);
  let canvasEl = $state<HTMLCanvasElement | null>(null);
  let stream = $state<MediaStream | null>(null);
  let recorder = $state<RecordingHandle | null>(null);
  let proxy: PoseRunnerProxy | null = null;
  let loop: LiveAnalysisLoop | null = null;
  type RecStatus = 'idle' | 'starting' | 'live' | 'recording' | 'processing' | 'error';
  let status = $state<RecStatus>('idle');
  let errorMsg = $state<string | null>(null);
  let elapsedMs = $state(0);
  let recStart = 0;
  let timer: ReturnType<typeof setInterval> | null = null;

  let setupOpen = $state(true);
  let matchConfig = $state<MatchConfig | null>(null);
  let matchState = $state<MatchState>(emptyMatchState());

  // Reactive mirrors of LiveAnalysisLoop state (populated via onFrame).
  let liveImpacts = $state<readonly CourtImpact[]>([]);
  let lastEvent = $state<CourtImpact | null>(null);
  let livePlayers = $state<LivePlayers>({});
  let liveBall = $state<LiveBall | null>(null);
  let calibState = $state<'pending' | 'converged' | 'failed'>('pending');
  let cutCount = $state(0);

  // Semi-automatic scoreboard
  let rallyPending = $state<Rally | null>(null);

  const settings = $derived(getSettings());

  onMount(async () => {
    status = 'starting';
    await initSettings();
    try {
      stream = await openRearCamera();
      if (videoEl) {
        videoEl.srcObject = stream;
        await videoEl.play();
      }
      proxy = new PoseRunnerProxy();
      await proxy.whenReady();
      // Live preview loop — pose + ball + calibration on the camera feed.
      if (videoEl) {
        loop = new LiveAnalysisLoop(
          { video: videoEl, canvas: canvasEl, proxy },
          {
            settings: snapshotSettings(),
            onFrame: (st) => {
              liveImpacts = st.liveImpacts;
              lastEvent = st.lastEvent;
              livePlayers = st.livePlayers;
              liveBall = st.liveBall;
              calibState = st.calibState;
              cutCount = st.cutCount;
            },
            onRallyEnd: (rally) => {
              // Only surface the toast in multiplayer mode — solo training
              // has no scoreboard to award against.
              if (matchConfig && !matchConfig.singlePlayer) {
                rallyPending = rally;
              }
            },
          },
        );
        loop.start();
      }
      status = 'live';
    } catch (err) {
      errorMsg = err instanceof Error ? err.message : String(err);
      status = 'error';
    }
  });

  onDestroy(() => {
    if (timer) clearInterval(timer);
    loop?.dispose();
    proxy?.dispose();
    stream?.getTracks().forEach((t) => t.stop());
  });

  $effect(() => {
    loop?.applySettings(settings);
  });

  function confirmSetup(cfg: MatchConfig) {
    matchConfig = cfg;
    setupOpen = false;
  }

  function skipSetup() {
    matchConfig = null;
    setupOpen = false;
  }

  function startRec() {
    if (!stream || !loop) return;
    // Reset accumulated state from the pre-recording preview.
    loop.reset();
    liveImpacts = [];
    lastEvent = null;
    cutCount = 0;
    recorder = startRecording(stream);
    recStart = performance.now();
    elapsedMs = 0;
    timer = setInterval(() => {
      elapsedMs = performance.now() - recStart;
    }, 100);
    status = 'recording';
  }

  async function stopRec() {
    if (!recorder || !loop) return;
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
    status = 'processing';
    const fps = recorder.fps();
    const blob = await recorder.stop();
    const durationMs = performance.now() - recStart;

    const handedness = matchConfig?.players[0]?.handedness ?? settings.handedness;
    const captured = loop.capturedFrames;
    const analyzed = analyzeSession(captured, fps, handedness);
    const sessionId = newSessionId();

    const v1Session = {
      id: sessionId,
      createdAtMs: Date.now(),
      durationMs,
      fps,
      videoBlob: blob,
      poseSequence: { frames: captured.slice(), fps, startTimeMs: Date.now() },
      courtTimeline: loop.courtTimeline.slice(),
      ...analyzed,
    };
    await db.sessions.add(v1Session);

    const players: Player[] = matchConfig?.players.length
      ? matchConfig.players.map(toArchivalPlayer)
      : [{ id: 'p1', displayName: 'Jogador', dominantSide: handedness }];

    let dispose: (() => void) | null = null;
    try {
      const bm = await import('../lib/bestMoments');
      const vb = bm.videoFromBlob(blob);
      dispose = vb.dispose;
      await bm.awaitVideoReady(vb.video);
      const input = bm.buildRawSessionInput({
        frames: captured,
        fps,
        contacts: analyzed.contacts,
        swings: analyzed.swings,
        players,
        matchState:
          matchConfig && !matchConfig.singlePlayer ? toArchivalMatchState(matchState) : null,
        durationMs,
        handedness,
        thumbnailSource: { kind: 'video', video: vb.video },
      });
      await bm.saveBestMomentsSession(input, { sessionId });
    } catch (err) {
      console.warn('v2 best-moments write failed', err);
    } finally {
      dispose?.();
    }

    push(`/swings/${sessionId}`);
  }

  function onRallyPick(winner: 'p1' | 'p2') {
    matchState = awardPoint(matchState, winner).state;
    rallyPending = null;
  }
  function onRallyDismiss() {
    rallyPending = null;
  }
</script>

<section class="relative h-full w-full overflow-hidden bg-black">
  <video
    bind:this={videoEl}
    class="absolute inset-0 h-full w-full object-cover"
    playsinline
    muted
    autoplay
  ></video>

  <canvas
    bind:this={canvasEl}
    class="pointer-events-none absolute inset-0 h-full w-full object-cover"
  ></canvas>

  <div class="absolute left-4 top-4 rounded-md bg-black/60 px-3 py-1 text-sm">
    {#if status === 'starting'}Iniciando câmera…
    {:else if status === 'live'}Pronto. Aperte gravar.
    {:else if status === 'recording'}● REC {(elapsedMs / 1000).toFixed(1)}s · {cutCount} cortes
    {:else if status === 'processing'}Processando…
    {:else if status === 'error'}Erro: {errorMsg}
    {/if}
  </div>

  <div class="absolute right-4 top-4 flex gap-2">
    <a
      href="#/settings"
      class="rounded-md bg-black/60 px-3 py-1 text-sm active:bg-black/80"
      aria-label="Configurações"
      title="Configurações"
    >⚙️</a>
    <a
      href="#/"
      class="rounded-md bg-black/60 px-3 py-1 text-sm active:bg-black/80"
    >
      Voltar
    </a>
  </div>

  {#if status === 'recording' && matchConfig && !matchConfig.singlePlayer}
    <div class="absolute right-4 top-16 pointer-events-auto">
      <Scoreboard bind:state={matchState} players={matchConfig.players} compact />
    </div>
  {/if}

  {#if settings.overlays.miniCourt && (status === 'live' || status === 'recording')}
    <div class="absolute left-4 bottom-24 w-56 pointer-events-none">
      <MiniQuadra
        compact
        impacts={liveImpacts}
        {lastEvent}
        {livePlayers}
        {liveBall}
        showTrail={settings.overlays.ballTrail}
        calibrationBadge={calibState === 'failed'
          ? 'Calibração manual em breve'
          : calibState === 'pending'
            ? 'Calibração em andamento…'
            : null}
      />
    </div>
  {/if}

  <div class="absolute inset-x-0 bottom-8 flex justify-center">
    {#if status === 'recording'}
      <button
        type="button"
        class="rounded-full bg-red-600 px-8 py-4 text-lg font-bold text-white shadow-2xl active:scale-95"
        onclick={stopRec}
      >
        Parar
      </button>
    {:else if status === 'live'}
      <button
        type="button"
        class="rounded-full bg-court px-8 py-4 text-lg font-bold text-slate-900 shadow-2xl active:scale-95"
        onclick={startRec}
      >
        Gravar
      </button>
    {/if}
  </div>

  {#if setupOpen && status !== 'error'}
    <PlayerSetup onStart={confirmSetup} onSkip={skipSetup} />
  {/if}

  {#if rallyPending}
    <RallyEndToast
      players={matchConfig?.players ?? []}
      onPick={onRallyPick}
      onDismiss={onRallyDismiss}
    />
  {/if}
</section>
